import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import {
  createEmployee,
  getEmployee,
  listDepts,
  listDesigs,
  listStates,
  listTerritories,
  listUsersLite,
  updateEmployee,
  type DeptRow,
  type DesigRow,
  type StateRow,
  type TerritoryRow,
  type UserLite,
} from '../../api/orgApi.js';
import { Field, FormShell, SaveBar, SelectField, todayIso } from './orgKit.js';

export function EmployeeFormPage(): ReactNode {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { empExtId } = useParams<{ empExtId: string }>();
  const editing = Boolean(empExtId);

  const [userId, setUserId] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [deptId, setDeptId] = useState('');
  const [desigId, setDesigId] = useState('');
  const [grade, setGrade] = useState('');
  const [joiningDate, setJoiningDate] = useState(todayIso());
  const [defaultTerritoryId, setDefaultTerritoryId] = useState('');
  const [officeLocation, setOfficeLocation] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [stateId, setStateId] = useState('');
  const [pincode, setPincode] = useState('');
  const [emergencyContactName, setEmergencyContactName] = useState('');
  const [emergencyContactMobile, setEmergencyContactMobile] = useState('');
  const [effectiveFromDate, setEffectiveFromDate] = useState(todayIso());

  const [users, setUsers] = useState<UserLite[]>([]);
  const [depts, setDepts] = useState<DeptRow[]>([]);
  const [desigs, setDesigs] = useState<DesigRow[]>([]);
  const [territories, setTerritories] = useState<TerritoryRow[]>([]);
  const [states, setStates] = useState<StateRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listUsersLite(token).then((r) => setUsers(r.data)).catch((e) => setError((e as Error).message));
    listDepts(token).then((r) => setDepts(r.data)).catch((e) => setError((e as Error).message));
    listDesigs(token).then((r) => setDesigs(r.data)).catch((e) => setError((e as Error).message));
    listTerritories(token).then((r) => setTerritories(r.data)).catch((e) => setError((e as Error).message));
    listStates(token).then((r) => setStates(r.data)).catch((e) => setError((e as Error).message));
  }, [token]);

  useEffect(() => {
    if (!empExtId) return;
    getEmployee(token, empExtId)
      .then((raw) => {
        const e = raw as Record<string, unknown>;
        const s = (k: string): string => (e[k] == null ? '' : String(e[k]));
        setUserId(s('userId'));
        setEmployeeCode(s('employeeCode'));
        setDeptId(s('deptId'));
        setDesigId(s('desigId'));
        setGrade(s('grade'));
        setJoiningDate(s('joiningDate') || todayIso());
        setDefaultTerritoryId(s('defaultTerritoryId'));
        setOfficeLocation(s('officeLocation'));
        setAddressLine1(s('addressLine1'));
        setAddressLine2(s('addressLine2'));
        setCity(s('city'));
        setStateId(s('stateId'));
        setPincode(s('pincode'));
        setEmergencyContactName(s('emergencyContactName'));
        setEffectiveFromDate(s('effectiveFromDate') || todayIso());
      })
      .catch((err) => setError((err as Error).message));
  }, [token, empExtId]);

  async function submit(): Promise<void> {
    if (!editing) {
      if (!userId) { setError(t('org.employee.form.userReq')); return; }
      if (!employeeCode.trim()) { setError(t('org.employee.form.codeReq')); return; }
      if (!joiningDate) { setError(t('org.employee.form.joinReq')); return; }
    }
    if (pincode && !/^[1-9][0-9]{5}$/.test(pincode)) { setError(t('org.employee.form.pincode6')); return; }
    if (emergencyContactMobile && !/^[6-9][0-9]{9}$/.test(emergencyContactMobile)) {
      setError(t('org.employee.form.mobile10'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      if (editing && empExtId) {
        await updateEmployee(token, empExtId, {
          deptId: deptId || undefined,
          desigId: desigId || undefined,
          grade: grade.trim() || undefined,
          defaultTerritoryId: defaultTerritoryId || undefined,
          officeLocation: officeLocation.trim() || undefined,
          addressLine1: addressLine1.trim() || undefined,
          addressLine2: addressLine2.trim() || undefined,
          city: city.trim() || undefined,
          stateId: stateId || undefined,
          pincode: pincode.trim() || undefined,
          emergencyContactName: emergencyContactName.trim() || undefined,
          emergencyContactMobile: emergencyContactMobile.trim() || undefined,
        });
        navigate(`/org/employees/${empExtId}`);
      } else {
        const res = await createEmployee(token, {
          userId,
          employeeCode: employeeCode.trim(),
          deptId: deptId || undefined,
          desigId: desigId || undefined,
          grade: grade.trim() || undefined,
          joiningDate,
          defaultTerritoryId: defaultTerritoryId || undefined,
          officeLocation: officeLocation.trim() || undefined,
          addressLine1: addressLine1.trim() || undefined,
          addressLine2: addressLine2.trim() || undefined,
          city: city.trim() || undefined,
          stateId: stateId || undefined,
          pincode: pincode.trim() || undefined,
          emergencyContactName: emergencyContactName.trim() || undefined,
          emergencyContactMobile: emergencyContactMobile.trim() || undefined,
          effectiveFromDate,
        });
        navigate(res?.empExtId ? `/org/employees/${res.empExtId}` : '/org/employees');
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const userOptions = users.map((u) => ({ value: u.userId, label: u.employeeCode ? `${u.displayName} (${u.employeeCode})` : u.displayName }));
  const deptOptions = depts.map((d) => ({ value: d.deptId, label: `${d.deptCode} — ${d.deptName}` }));
  const desigOptions = useMemo(
    () => desigs.filter((d) => !deptId || !d.deptId || d.deptId === deptId).map((d) => ({ value: d.desigId, label: `${d.desigCode} — ${d.desigName}` })),
    [desigs, deptId],
  );
  const territoryOptions = territories.map((t) => ({ value: t.territoryId, label: `${t.territoryCode} — ${t.territoryName}` }));
  const stateOptions = states.map((s) => ({ value: s.stateId, label: `${s.stateCode} — ${s.stateName}` }));

  return (
    <FormShell
      label="employee-form"
      title={editing ? t('org.employee.form.editTitle') : t('org.employee.form.newTitle')}
      error={error}
      onSubmit={submit}
    >
      {!editing && (
        <SelectField label={t('org.employee.form.userAccount')} name="userId" value={userId} onChange={setUserId} options={userOptions} required placeholder={t('org.employee.form.userPh')} />
      )}
      <Field label={t('org.employee.form.employeeCode')} name="employeeCode" value={employeeCode} onChange={editing ? () => undefined : setEmployeeCode} required={!editing} placeholder={t('org.employee.form.codePh')} />
      <SelectField label={t('org.field.department')} name="deptId" value={deptId} onChange={setDeptId} options={deptOptions} placeholder={t('org.common.noneNone')} />
      <SelectField label={t('org.field.designation')} name="desigId" value={desigId} onChange={setDesigId} options={desigOptions} placeholder={t('org.common.noneNone')} />
      <Field label={t('org.field.grade')} name="grade" value={grade} onChange={setGrade} />
      <Field label={t('org.employee.form.joiningDate')} name="joiningDate" value={joiningDate} onChange={setJoiningDate} type="date" required={!editing} />
      <SelectField label={t('org.employee.form.defaultTerritory')} name="defaultTerritoryId" value={defaultTerritoryId} onChange={setDefaultTerritoryId} options={territoryOptions} placeholder={t('org.common.noneNone')} />
      <Field label={t('org.employee.form.officeLocation')} name="officeLocation" value={officeLocation} onChange={setOfficeLocation} />
      <Field label={t('org.employee.form.address1')} name="addressLine1" value={addressLine1} onChange={setAddressLine1} />
      <Field label={t('org.employee.form.address2')} name="addressLine2" value={addressLine2} onChange={setAddressLine2} />
      <Field label={t('org.employee.form.city')} name="city" value={city} onChange={setCity} />
      <SelectField label={t('org.field.state')} name="stateId" value={stateId} onChange={setStateId} options={stateOptions} placeholder={t('org.common.noneNone')} />
      <Field label={t('org.employee.form.pincode')} name="pincode" value={pincode} onChange={setPincode} placeholder={t('org.employee.form.pincodePh')} />
      <Field label={t('org.employee.form.emergencyName')} name="emergencyContactName" value={emergencyContactName} onChange={setEmergencyContactName} />
      <Field label={t('org.employee.form.emergencyMobile')} name="emergencyContactMobile" value={emergencyContactMobile} onChange={setEmergencyContactMobile} placeholder={t('org.employee.form.mobilePh')} />
      {!editing && (
        <Field label={t('org.field.effectiveFrom')} name="effectiveFromDate" value={effectiveFromDate} onChange={setEffectiveFromDate} type="date" required />
      )}
      <SaveBar busy={busy} saveLabel={editing ? t('org.common.saveChanges') : t('org.employee.form.create')} cancelTo={editing && empExtId ? `/org/employees/${empExtId}` : '/org/employees'} />
    </FormShell>
  );
}
